import React, { useEffect, useState, useRef } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useTranslation } from 'react-i18next';
import * as signalR from '@microsoft/signalr';
import { useSignalR } from '../../Helpers/SignalRHelper';
import { Card, ProgressBar, Dialog, FileUpload, AxiosProgressEvent, FileUploadHandlerEvent, SCIcon, Button } from '../../Common/Imports';
import { DataImportListTodo } from './DataImport_TodoList';
import { ApiResponse, ImportData_AggregationInfo } from '../../Models/Classes';

export const DataImportList: React.FC = () => {
    const { t } = useTranslation();
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [showProgress, setShowProgress] = useState<boolean>(false);
    const fileUploadRef = useRef<FileUpload>(null);
    const [progress, setProgress] = useState<number>(0);
    const [RunProgressVisible, setRunProgressVisible] = useState<boolean>(false);
    const AxiosHelper = useAxios();
    const { ProgressConnection } = useSignalR();
    const [listKey, setListKey] = useState(0);
    const [totals, setTotals] = useState<ImportData_AggregationInfo | undefined>(undefined);
    const [totalsLoading, setTotalsLoading] = useState<boolean>(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const refreshTodoListComponent = () => {
        setListKey(prev => prev + 1);
        setRefreshKey(prev => prev + 1);
    };

    useEffect(() => {
        const setupConnections = async () => {
            ProgressConnection?.on('ReceiveMessage', (message: number) => {
                setProgress(message);
            });

            if (ProgressConnection?.state === signalR.HubConnectionState.Disconnected) {
                try {
                    await ProgressConnection.start();
                } catch (err) {
                    console.error('Notification SignalR Connection Error: ', err);
                }
            }
        };

        setupConnections();

        return () => {
            ProgressConnection?.off('ReceiveMessage');
        };
    }, [ProgressConnection]);

    useEffect(() => {
        const getTotals = async () => {
            setTotalsLoading(true);
            try {
                const resp = await AxiosHelper.get<ApiResponse<ImportData_AggregationInfo>>(`Imports/GetList`);
                if (resp.data.code === 0) {
                    setTotals(resp.data.data);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setTotalsLoading(false);
            }
        };
        getTotals();
    }, [AxiosHelper, refreshKey]);

    const handleDownload = (file: string) => {
        const link = document.createElement('a');
        link.href = '/' + file;
        link.download = file;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const onUploadProgress = (event: AxiosProgressEvent) => {
        setUploadProgress(Math.round((event.loaded * 100) / (event.total || 100)));
    };

    const onUploadStart = () => {
        setShowProgress(true);
        setUploadProgress(0);
    };

    const onUploadEnd = () => {
        setShowProgress(false);
        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }
    };

    const UploadFile = async (event: FileUploadHandlerEvent) => {
        onUploadStart();
        const formData = new FormData();
        formData.append('formFile', event.files[0]);

        try {
            const response = await AxiosHelper.post('/imports/uploadnewimport', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: onUploadProgress,
            });
            if (response.status === 200) {
                onUploadEnd();
            } else {
                setShowProgress(false);
                if (fileUploadRef.current) {
                    fileUploadRef.current.clear();
                }
            }
        } catch {
            setShowProgress(false);
            if (fileUploadRef.current) {
                fileUploadRef.current.clear();
            }
        }
    };

    function CardHeader() {
        return (
            <div className="imports-page-header">
                <div className="imports-page-titlebar">
                    <div>
                        <h1>All imports</h1>
                        <p>Review import errors, warnings and records waiting to be processed.</p>
                    </div>
                    <div className="imports-page-actions">
                        <Button
                            type="button"
                            label={t("Common.Buttons.Refresh")}
                            icon="pi pi-refresh"
                            outlined
                            severity="secondary"
                            loading={totalsLoading}
                            disabled={showProgress}
                            onClick={refreshTodoListComponent}
                            className="imports-action-button imports-refresh-button"
                        />
                        <FileUpload
                            ref={fileUploadRef}
                            mode="basic"
                            name="demo[]"
                            url="/api/upload"
                            maxFileSize={1000000}
                            accept=".csv"
                            customUpload
                            uploadHandler={UploadFile}
                            chooseLabel={t("Common.Buttons.Upload")}
                            chooseOptions={{
                                icon: 'pi pi-upload',
                                className: 'p-button-success imports-action-button imports-upload-button'
                            }}
                            disabled={showProgress}
                            className="imports-upload"
                        />
                    </div>
                </div>
                <div className="imports-upload-help" role="note" aria-label="CSV upload requirements">
                    <SCIcon Name="Info" Classes="imports-upload-help-icon" />
                    <div>
                        <strong>CSV upload requirements</strong>
                        <p>The file must not contain a header row and must use a semicolon (;) as separator.</p>
                        <div className="imports-help-links">
                            <button type="button" onClick={() => handleDownload('Import_Infos.xlsx')}>Field documentation</button>
                            <button type="button" onClick={() => handleDownload('Import_Example.csv')}>Download CSV example</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <Dialog header="Header" visible={RunProgressVisible} style={{ width: '50vw' }} onHide={() => { if (!RunProgressVisible) return; setRunProgressVisible(false); setProgress(0) }}>
                <p className="m-0">
                    <ProgressBar value={progress}></ProgressBar>
                </p>
            </Dialog>
            <Dialog header={t("Uploading...")} visible={showProgress} modal onHide={() => setShowProgress(false)}>
                <ProgressBar value={uploadProgress} />
                <p>{uploadProgress}%</p>
            </Dialog>
            <Card header={CardHeader} className="imports-page">
                {
                    totals &&
                    <div className="imports-summary" aria-label="Import summary">
                        {[
                            { icon: "Error", color: "danger", label: t("DataImport:Errors") || "Errori", count: totals?.errors ?? 0 },
                            { icon: "Warning", color: "warning", label: t("DataImport:Warning") || "Avvisi", count: totals?.warnings ?? 0 },
                            { icon: "Queued", color: "info", label: t("DataImport:Queued") || "In attesa", count: totals?.toDo ?? 0 },
                        ].map(({ icon, color, label, count }, i) => (
                            <div key={i} className={`imports-summary-card imports-summary-${color}`}>
                                <div>
                                    <SCIcon Name={icon} Classes={`text-${color} fs-3`} />
                                </div>
                                <div>
                                    {
                                        totalsLoading ?
                                            <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar> :
                                            <><span>{label}</span><strong>{count.toLocaleString()}</strong></>
                                    }
                                </div>
                            </div>
                        ))}
                    </div>
                }
                <div className="row">
                    <div className="col-md-12">
                        <DataImportListTodo key={listKey} onDataChanged={refreshTodoListComponent} />
                    </div>
                </div>
            </Card>
        </>
    );
};

export default DataImportList;
